/**
 * Selfie absensi (item 9, review 12 Sep 2026) — bagian MURNI, tanpa kamera dan tanpa canvas.
 *
 * Dipisahkan dari komponen persis alasan `signature.ts`: fungsi di sini dapat diuji tanpa DOM.
 * Orkestrasi kamera/canvas sungguhan hidup di `SelfieCapture.tsx` dan diverifikasi lewat
 * pengujian browser manual, bukan `node --test`.
 */

/** Sisi panjang maksimum hasil kompresi — spec Owner (Tahap 2 item 9). */
export const SELFIE_MAX_LONG_EDGE_PX = 640;

/** Target ukuran berkas — cukup untuk wajah dikenali, murah untuk 100+ orang/hari (~46 MB/bulan). */
export const SELFIE_TARGET_MIN_BYTES = 40 * 1024;
export const SELFIE_TARGET_MAX_BYTES = 80 * 1024;

/** Kualitas JPEG awal. Diturunkan bertahap bila hasil pertama masih di atas target. */
export const SELFIE_INITIAL_JPEG_QUALITY = 0.7;
const QUALITY_STEP = 0.1;
const MIN_JPEG_QUALITY = 0.3;

/**
 * Dimensi tujuan menjaga rasio aspek, sisi PANJANG dipangkas ke `maxLongEdge`.
 * Foto yang sudah lebih kecil dari batas dibiarkan apa adanya — memperbesar tidak menambah data,
 * hanya membengkakkan berkas.
 */
export function scaledSelfieDimensions(
  width: number,
  height: number,
  maxLongEdge: number = SELFIE_MAX_LONG_EDGE_PX,
): { readonly width: number; readonly height: number } {
  const longEdge = Math.max(width, height);
  if (longEdge <= maxLongEdge || longEdge <= 0) {
    return { width, height };
  }
  const scale = maxLongEdge / longEdge;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/**
 * Kebijakan tangga kualitas — MURNI, dipisah dari mekanisme `canvas.toBlob` yang menghasilkan
 * byte sungguhan. Menjawab "kualitas apa yang dicoba berikutnya", bukan menghasilkan gambarnya.
 *
 * `null` berarti MENYERAH: hasil terakhir dipakai apa adanya, meski di atas target. Kompresi
 * bertahan bukan tujuan — orang yang absen tidak boleh disandera loop yang tidak pernah selesai
 * karena foto latar rumit yang tidak pernah turun ke bawah target.
 */
export function nextCompressionQuality(
  currentQuality: number,
  resultBytes: number,
  targetMaxBytes: number = SELFIE_TARGET_MAX_BYTES,
): number | null {
  if (resultBytes <= targetMaxBytes) {
    return null; // sudah cukup kecil — tidak perlu mencoba lagi
  }
  const next = Math.round((currentQuality - QUALITY_STEP) * 10) / 10;
  if (next < MIN_JPEG_QUALITY) {
    return null; // sudah di kualitas terendah yang layak — pakai apa adanya
  }
  return next;
}

/**
 * Alasan absen TANPA selfie (jalur gagal kamera) — SATU-SATUNYA jalan lolos gerbang server saat
 * `selfiePolicy` REQUIRED. `.trim()` mencerminkan pemeriksaan server persis (`attendance.service.ts`)
 * — string berisi spasi saja bukan alasan, dan pengecekan yang berbeda di sisi klien dan server
 * akan membuat tombol tampak aktif padahal server tetap menolak.
 */
export function noSelfieReasonValid(reason: string): boolean {
  return reason.trim().length > 0;
}

export type SelfiePhase =
  'idle' | 'compressing' | 'hashing' | 'requesting-target' | 'uploading' | 'uploaded' | 'failed';

export interface SelfieSlot {
  readonly phase: SelfiePhase;
  /** Diisi server saat `upload-targets` dijawab. `null` sampai saat itu. */
  readonly fileId: string | null;
  /** 0..100, hanya bermakna saat `uploading`. */
  readonly progress: number;
  readonly errorMessage: string | null;
  /** Ukuran SETELAH kompresi — untuk diagnostik, bukan untuk keputusan server. */
  readonly compressedBytes: number | null;
}

export const SELFIE_IDLE: SelfieSlot = {
  phase: 'idle',
  fileId: null,
  progress: 0,
  errorMessage: null,
  compressedBytes: null,
};

export function advanceSelfie(
  slot: SelfieSlot,
  phase: SelfiePhase,
  patch: Partial<Pick<SelfieSlot, 'fileId' | 'compressedBytes'>> = {},
): SelfieSlot {
  return {
    ...slot,
    phase,
    fileId: patch.fileId ?? slot.fileId,
    compressedBytes: patch.compressedBytes ?? slot.compressedBytes,
    errorMessage: phase === 'failed' ? slot.errorMessage : null,
    progress: phase === 'uploaded' ? 100 : slot.progress,
  };
}

export function markSelfieFailed(slot: SelfieSlot, message: string): SelfieSlot {
  return { ...slot, phase: 'failed', errorMessage: message };
}

/** Mengulang jepretan — kembali ke awal, BUKAN melanjutkan dari tengah (target lama kedaluwarsa). */
export function retrySelfie(): SelfieSlot {
  return SELFIE_IDLE;
}

export function withSelfieProgress(slot: SelfieSlot, progress: number): SelfieSlot {
  return { ...slot, progress: Math.max(0, Math.min(100, progress)) };
}

export function isSelfieUploaded(slot: SelfieSlot): boolean {
  return slot.phase === 'uploaded' && slot.fileId !== null;
}

export function isSelfieBusy(slot: SelfieSlot): boolean {
  return (
    slot.phase === 'compressing' ||
    slot.phase === 'hashing' ||
    slot.phase === 'requesting-target' ||
    slot.phase === 'uploading'
  );
}
